import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContentMaxPriority,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { useUpdateItemPedido } from '@/hooks/useUpdateItemPedido';
import { useObrigatoriedadesItem } from '@/hooks/useObrigatoriedadesItem';
import { formatCurrencyInput, parseCurrencyInput } from '@/lib/formatters';
import { toast } from 'sonner';
import { Package, AlertTriangle } from 'lucide-react';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  item: any;
  pedidoId: string;
  onSuccess?: () => void;
}

export function EditarItemCompletoModal({ 
  open, 
  onOpenChange, 
  item, 
  pedidoId,
  onSuccess 
}: Props) {
  const [salvando, setSalvando] = useState(false);
  const { mutate: updateItem } = useUpdateItemPedido();
  
  // Estados para os campos
  const [planoNome, setPlanoNome] = useState('');
  const [planoValor, setPlanoValor] = useState<number | null>(null);
  const [quantidade, setQuantidade] = useState(1);
  const [valorTotal, setValorTotal] = useState('');
  const [produtoId, setProdutoId] = useState<string | null>(null);

  // Buscar obrigatoriedades do produto para detectar se usa valor_total
  const { data: obrigatoriedades } = useObrigatoriedadesItem(
    produtoId,
    'negociacao',
    true
  );

  const produtoUsaValorTotal = obrigatoriedades?.some(
    o => o.obrigatoriedades_catalogo.codigo === 'valor_total'
  ) ?? false;

  // Carregar dados do item ao abrir
  useEffect(() => {
    if (open && item) {
      setPlanoNome(item.plano_nome || '');
      setQuantidade(item.quantidade || 1);
      setProdutoId(item.produto_id || null);
      
      // Carregar valor unitário
      const valorUnitario = item.valor_unit ?? item.plano_valor ?? 0;
      setPlanoValor(valorUnitario);
      
      // Calcular e formatar o valor total
      const totalCalculado = (item.quantidade || 1) * valorUnitario;
      const totalFormatado = formatCurrencyInput(totalCalculado);
      setValorTotal(totalFormatado);
    }
  }, [open, item]);

  const handleSalvar = async () => {
    // Validação: valor não pode ser null ou negativo
    if (planoValor === null || planoValor < 0) {
      toast.error('O valor do plano deve ser maior ou igual a zero');
      return;
    }

    setSalvando(true);
    try {
      const updates = {
        plano_nome: planoNome,
        plano_valor: planoValor ?? 0,
        valor_unit: planoValor ?? 0,
        quantidade: quantidade
      };

      updateItem({
        itemId: item.id,
        updates
      }, {
        onSuccess: () => {
          toast.success('Item atualizado com sucesso!');
          onSuccess?.();
          onOpenChange(false);
        },
        onError: (error: any) => {
          toast.error(`Erro ao atualizar: ${error.message}`);
        },
        onSettled: () => {
          setSalvando(false);
        }
      });
    } catch (error: any) {
      toast.error(`Erro ao salvar: ${error.message}`);
      setSalvando(false);
    }
  };

  // Calcular subtotal
  const subtotal = (planoValor ?? 0) * quantidade;
  
  // Validação para desabilitar botão
  const isValorInvalido = planoValor === null || planoValor < 0;

  // Verificar se o produto é MP MÓVEL
  const isMpMovel = item?.produto_nome?.toUpperCase().includes('MP MÓVEL');

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContentMaxPriority className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Package className="w-5 h-5" />
            Editar Item
          </DialogTitle>
          <div className="flex items-center gap-2 mt-2">
            <Badge variant="outline">{item?.produto_nome || 'Produto'}</Badge>
            {item?.produto?.familia && (
              <Badge variant="secondary">{item.produto.familia}</Badge>
            )}
          </div>
        </DialogHeader>

        {isMpMovel && (
          <Alert className="bg-primary/5 border-primary dark:bg-primary dark:border-primary">
            <AlertTriangle className="h-4 w-4 text-primary dark:text-primary" />
            <AlertTitle className="text-primary dark:text-primary">
              Atenção - Migração de Plano
            </AlertTitle>
            <AlertDescription className="text-primary dark:text-primary">
              Incluir o pacote total de GB da conta quando por Migração de Plano
            </AlertDescription>
          </Alert>
        )}

        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label htmlFor="plano_nome">Nome do Plano</Label>
            <Input
              id="plano_nome"
              value={planoNome}
              onChange={(e) => setPlanoNome(e.target.value)}
              placeholder="Ex: Plano 100GB"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            {produtoUsaValorTotal ? (
              <div className="space-y-2">
                <Label htmlFor="valor_total">Valor Total (R$)</Label>
                <Input
                  id="valor_total"
                  type="text"
                  placeholder="R$ 0,00"
                  value={valorTotal}
                  onChange={(e) => {
                    const textoDigitado = e.target.value;
                    setValorTotal(textoDigitado);
                    const valorTotalNum = parseCurrencyInput(textoDigitado);
                    const novoValorUnit = quantidade > 0 ? valorTotalNum / quantidade : 0;
                    setPlanoValor(novoValorUnit);
                  }}
                  onBlur={() => {
                    const valorTotalNum = parseCurrencyInput(valorTotal);
                    if (valorTotalNum && valorTotalNum >= 0) {
                      const valorFormatado = formatCurrencyInput(valorTotalNum);
                      setValorTotal(valorFormatado);
                      const novoValorUnit = quantidade > 0 ? valorTotalNum / quantidade : 0;
                      setPlanoValor(parseFloat(novoValorUnit.toFixed(2)));
                    } else {
                      setValorTotal('R$ 0,00');
                      setPlanoValor(0);
                    }
                  }}
                  onFocus={() => {
                    const valorTotalNum = parseCurrencyInput(valorTotal);
                    if (valorTotalNum > 0) {
                      setValorTotal(valorTotalNum.toFixed(2).replace('.', ','));
                    }
                  }}
                />
                <p className="text-xs text-muted-foreground">
                  Valor total do pacote (fixo ao mudar quantidade)
                </p>
              </div>
            ) : (
              <div className="space-y-2">
                <Label htmlFor="plano_valor">Valor Unitário (R$)</Label>
                <Input
                  id="plano_valor"
                  type="number"
                  step="0.01"
                  value={planoValor ?? ''}
                  onChange={(e) => {
                    const value = e.target.value.trim();
                    if (value === '' || value === null) {
                      setPlanoValor(null);
                    } else {
                      const parsed = parseFloat(value);
                      setPlanoValor(isNaN(parsed) ? null : parsed);
                    }
                  }}
                  placeholder="0.00"
                />
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="quantidade">Quantidade</Label>
              <Input
                id="quantidade"
                type="number"
                min="1"
                value={quantidade}
                onChange={(e) => {
                  const novaQuantidade = parseInt(e.target.value) || 1;
                  setQuantidade(novaQuantidade);
                  
                  // Se usa valor_total, recalcular valor_unit mantendo o total
                  if (produtoUsaValorTotal && valorTotal) {
                    const valorTotalNum = parseCurrencyInput(valorTotal);
                    const novoValorUnit = novaQuantidade > 0 ? valorTotalNum / novaQuantidade : 0;
                    setPlanoValor(novoValorUnit);
                  }
                }}
              />
            </div>
          </div>

          {planoValor !== null && planoValor >= 0 && (
            <div className="p-3 bg-muted rounded-md">
              <div className="flex justify-between items-center">
                <span className="text-sm font-medium">
                  {produtoUsaValorTotal ? 'Valor Total:' : 'Subtotal:'}
                </span>
                <span className="text-lg font-bold">
                  {new Intl.NumberFormat('pt-BR', {
                    style: 'currency',
                    currency: 'BRL'
                  }).format(produtoUsaValorTotal ? parseCurrencyInput(valorTotal) : subtotal)}
                </span>
              </div>
              {produtoUsaValorTotal && (
                <div className="text-xs text-muted-foreground mt-1">
                  Valor unitário: {new Intl.NumberFormat('pt-BR', {
                    style: 'currency',
                    currency: 'BRL'
                  }).format(planoValor ?? 0)}
                </div>
              )}
            </div>
          )}
        </div>

        <DialogFooter className="gap-2">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={salvando}
          >
            Cancelar
          </Button>
          <Button onClick={handleSalvar} disabled={salvando || isValorInvalido}>
            {salvando ? 'Salvando...' : 'Salvar'}
          </Button>
        </DialogFooter>
      </DialogContentMaxPriority>
    </Dialog>
  );
}
